import { OfflinePurchase } from '@@types'
import { gql, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

type Query = {
  offlinePurchases: OfflinePurchase[]
}

type Variables = {
  from?: number
  to?: number
  limit?: number
  offset?: number
}


export const GQL_QUERY_OFFLINE_PURCHASES = gql`
  query offlinePurchases (
    $from: Timestamp
    $to: Timestamp
    $limit: Int
    $offset: Int
  ) {
    offlinePurchases(
      from: $from
      to: $to
      limit: $limit
      offset: $offset
    ) {
      id
      created_at
      placed_at
      status
      total
      saledCost
      customerFinalCost
      deliveryCost
      address
      comment
      payment_status
      payment_type
      assembled_total
      totalCashback
      totalDiscount
      deliveryType
      reviewInvite
      placedAt
    }
  }
`

export const useFetchOfflinePurchases = ({
  from,
  to,
  limit = 10
}: Variables = {}) => {
  const [ loadingMore, setLoadingMore ] = useState(false)
  const [ hasMore, setHasMore ] = useState(true)
  const [ offset, setOffset ] = useState(0)

  const { data, loading, error, refetch, fetchMore } = useQuery<Query, Variables>(GQL_QUERY_OFFLINE_PURCHASES, {
    variables: {
      from,
      to,
      limit
    },
    fetchPolicy: 'cache-and-network',
    onCompleted (data) {
      if (data.offlinePurchases.length < limit) {
        setHasMore(false)
      }
    }
  })

  const orders = useMemo(() => data?.offlinePurchases ?? [], [ data?.offlinePurchases ])

  const refresh = async () => {
    if (refetch) {
      setOffset(0)
      await refetch()
    }
  }

  const fetchNextPage = async () => {
    if (!loading && !loadingMore && hasMore) {
      setLoadingMore(true)
      await fetchMore({
        variables: {
          limit,
          offset: offset + limit
        },
        updateQuery (previousResult: Query, { fetchMoreResult }) {
          const prev = previousResult.offlinePurchases ?? []
          const curr = fetchMoreResult.offlinePurchases ?? []

          setOffset(offset + curr.length)

          if (curr.length < limit) {
            setHasMore(false)
          }

          if (curr.length === 0) {
            return previousResult
          }

          return {
            ...previousResult,
            offlinePurchases: [
              ...prev,
              ...curr
            ]
          }
        }
      })
      setLoadingMore(false)
    }
  }

  return {
    orders,
    data,
    loading,
    error,
    hasMore,
    refresh,
    refetch,
    fetchNextPage
  }
}